import React from 'react';
import { Box } from '@mui/material';
import { ResponsiveBar } from '@nivo/bar';
import { formatNumberWithoutDecimals } from '../../utils/formatters';
import { colors } from '../../styles/theme';

interface PValueChartProps {
  bruttoertrag: number;
  p50?: number;
  p75?: number;
  p90?: number;
  title?: string;
  height?: string;
  /** Der aktuell ausgewählte Ertragswert (Bruttoertrag, P50, P75, P90) */
  activePValue?: string;
  /** Callback wenn auf einen Balken geklickt wird */
  onBarClick?: (pValue: string) => void;
}

const PValueChart: React.FC<PValueChartProps> = ({
  bruttoertrag,
  p50,
  p75,
  p90,
  height = '300px',
  activePValue,
  onBarClick
}) => {
  const [hoveredBar, setHoveredBar] = React.useState<string | null>(null);

  // Daten für das Diagramm vorbereiten - nur Werte die vorhanden sind
  // Umgekehrte Reihenfolge: Bruttoertrag oben
  const data = [
    p90 && p90 > 0 && {
      id: 'P90',
      label: 'P90',
      value: p90,
      color: colors.chart.p90
    },
    p75 && p75 > 0 && {
      id: 'P75',
      label: 'P75',
      value: p75,
      color: colors.chart.p75
    },
    p50 && p50 > 0 && {
      id: 'P50',
      label: 'P50',
      value: p50,
      color: colors.chart.p50
    },
    bruttoertrag > 0 && {
      id: 'Bruttoertrag',
      label: 'Bruttoertrag',
      value: bruttoertrag,
      color: colors.chart.bruttoertrag
    }
  ].filter(Boolean) as Array<{ id: string; label: string; value: number; color: string }>;

  return (
    <Box>
      {/* Chart Container mit Opacity-basiertem Hover-Effekt */}
      <Box
        sx={{
          width: '100%',
          height: height,
          backgroundColor: 'transparent',
          position: 'relative',
          overflow: 'hidden',
        }}
        onMouseLeave={() => setHoveredBar(null)}
      >
        <ResponsiveBar
          data={data}
          keys={['value']}
          indexBy="label"
          layout="horizontal"
          padding={0.4}
          margin={{ top: 0, right: 10, bottom: 0, left: 10 }}
          valueScale={{ type: 'linear' }}
          indexScale={{ type: 'band', round: true }}
          colors={(d) => d.data.color}
          borderRadius={15}
          borderWidth={0}
          theme={{
            axis: {
              ticks: {
                text: {
                  fontSize: 14,
                  fontWeight: 500,
                }
              }
            },
            labels: {
              text: {
                fontSize: 15,
                fontWeight: 500,
              }
            }
          }}
          axisTop={null}
          axisRight={null}
          axisBottom={null}
          axisLeft={null}
          labelSkipWidth={12}
          labelSkipHeight={12}
          labelTextColor={colors.chart.text}
          // Labels deaktiviert, da wir custom Layer verwenden
          enableLabel={false}
          // Hover- und Klick-Events mit Nivo-Bordmitteln
          onMouseEnter={(bar) => setHoveredBar(bar.indexValue as string)}
          onClick={(bar) => onBarClick?.(bar.indexValue as string)}
          // Custom Layers für Dimming-Overlay, Aktiv-Indikator und Labels
          layers={[
            'bars',  // Nivo rendert die Balken mit Animation
            'axes',
            'markers',
            'legends',
            'annotations',
            // Dimming-Overlay für Hover-Effekt
            ({ bars }) => (
              <>
                {bars.map((bar) => {
                  const isHovered = hoveredBar === bar.data.indexValue;
                  const shouldDim = hoveredBar !== null && !isHovered;

                  return (
                    <rect
                      key={`overlay-${bar.key}`}
                      x={bar.x}
                      y={bar.y}
                      width={bar.width}
                      height={bar.height}
                      fill="white"
                      opacity={shouldDim ? 0.5 : 0}
                      rx={15}
                      ry={15}
                      style={{
                        transition: 'opacity 0.2s ease-in-out',
                        pointerEvents: 'none',
                      }}
                    />
                  );
                })}
              </>
            ),
            // Aktiv-Indikator Layer (goldener Rahmen für aktiven Balken)
            ({ bars }) => (
              <>
                {bars.map((bar) => {
                  const isActive = activePValue === bar.data.indexValue;
                  if (!isActive) return null;

                  return (
                    <rect
                      key={`active-${bar.key}`}
                      x={bar.x - 1.5}
                      y={bar.y - 1.5}
                      width={bar.width + 3}
                      height={bar.height + 3}
                      fill="none"
                      stroke={colors.chart.selected}
                      strokeWidth={3}
                      rx={16.5}
                      ry={16.5}
                      style={{
                        pointerEvents: 'none',
                      }}
                    />
                  );
                })}
              </>
            ),
            // Label-Layer mit Checkmark für aktiven Balken
            ({ bars }) => (
              <>
                {bars.map((bar) => {
                  const isActive = activePValue === bar.data.indexValue;

                  return (
                    <g key={`text-group-${bar.key}`}>
                      <text
                        x={bar.x + 10}
                        y={bar.y + bar.height / 2}
                        textAnchor="start"
                        dominantBaseline="central"
                        style={{
                          fill: '#333',
                          fontSize: '15px',
                          pointerEvents: 'none',
                        }}
                      >
                        <tspan style={{ fontWeight: 400 }}>
                          {bar.data.indexValue}:
                        </tspan>
                        <tspan style={{ fontWeight: 600 }}>
                          {' '}{formatNumberWithoutDecimals(bar.data.value as number)} MWh
                        </tspan>
                      </text>
                      {/* Checkmark für aktiven Balken */}
                      {isActive && (
                        <g transform={`translate(${bar.x + bar.width - 30}, ${bar.y + bar.height / 2 - 10})`}>
                          <circle cx="10" cy="10" r="10" fill={colors.chart.selected} />
                          <path
                            d="M6 10 L9 13 L14 7"
                            stroke="white"
                            strokeWidth="2"
                            fill="none"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </g>
                      )}
                    </g>
                  );
                })}
              </>
            ),
          ]}
          animate={true}
          motionConfig="wobbly"
          tooltip={() => null}
          role="application"
          ariaLabel="P-Werte Balkendiagramm"
          barAriaLabel={(e) => `${e.id}: ${formatNumberWithoutDecimals(e.value as number)} MWh`}
        />
      </Box>
    </Box>
  );
};

export default PValueChart;
